import { X } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import type { ReactNode } from 'react'
import { Button } from '@/shared/ui/button'

/**
 * Modal shell shared by the attachment gallery and the document preview.
 * Radix traps focus, restores it on close and closes on Escape.
 */
export function PreviewDialog({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  onArrowKey,
  children,
  footer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  closeLabel: string
  /** Called for ArrowLeft (-1) and ArrowRight (1) outside text inputs. */
  onArrowKey?: (offset: -1 | 1) => void
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/75" />
        <DialogPrimitive.Content
          className="fixed inset-2 z-50 flex flex-col items-center gap-3 overflow-hidden rounded-lg bg-background p-2 shadow-xl sm:inset-10 sm:p-4"
          onKeyDown={(event) => {
            if (
              !onArrowKey ||
              event.target instanceof HTMLInputElement ||
              event.target instanceof HTMLTextAreaElement
            )
              return
            if (event.key === 'ArrowLeft') onArrowKey(-1)
            if (event.key === 'ArrowRight') onArrowKey(1)
          }}
        >
          <DialogPrimitive.Title className="sr-only">
            {title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {description}
          </DialogPrimitive.Description>
          <DialogPrimitive.Close asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 z-10"
              aria-label={closeLabel}
            >
              <X className="size-5" />
            </Button>
          </DialogPrimitive.Close>
          {children}
          {footer}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
